import { readErrorMessage } from '@Components/util/utils'
import { RootState } from '@AppState/redux-store'
import { GamesServiceAPI } from '@API/data-services/games-service'
import { GameBrandingUpdateRequest } from '@Generated/back-office-client/src'
import { Container, Flex, Heading, Section, Skeleton, Text } from '@radix-ui/themes'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { AppearanceSection } from './form-sections/appearance-section'
import { PromotionalMessageList } from './promotional-message-list'
import {
  COLOR_FIELDS,
  GAME_LOAD_FAILED_MESSAGE,
  GAME_NOT_FOUND_MESSAGE,
  SAVE_FAILED_MESSAGE,
} from './constants'
import { BrandingErrors, ColorField, EditableGame, GameBrandingFormData } from './types'
import { useEditableGames } from './use-editable-games'
import { blankToNull, editableGamesQueryKey, isValidHexColor } from './utils'
import './styles.scss'

const HEX_COLOR_ERROR = 'Enter a colour as #RRGGBB, or leave it blank to use the default.'

const toBrandingFormData = (game: EditableGame): GameBrandingFormData => ({
  logoUrl: game.branding?.logoUrl ?? '',
  brandColorPrimary: game.branding?.brandColorPrimary ?? '',
  fontColorPrimary: game.branding?.fontColorPrimary ?? '',
  specialBallColor: game.branding?.specialBallColor ?? '',
})

export const GamePage = () => {
  const { gameId = '' } = useParams()
  const { data: games, isLoading, error: loadError } = useEditableGames()
  const game = games?.find((candidate) => candidate.id === gameId)

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="4">
          <Link to=".." relative="path">
            <Text size="2">← Back to games</Text>
          </Link>

          {isLoading && (
            <Skeleton loading={true}>
              <Flex direction="column" gap="2" data-testid="game-loading-skeleton">
                <div className="skeleton-card" />
                <div className="skeleton-card" />
              </Flex>
            </Skeleton>
          )}

          {loadError && (
            <Text size="2" color="red" data-testid="game-load-error">
              {GAME_LOAD_FAILED_MESSAGE}
            </Text>
          )}

          {/* Only when the load succeeded — a failure says nothing about whether the game exists. */}
          {!isLoading && !loadError && !game && (
            <Text size="2" color="gray" data-testid="game-not-found">
              {GAME_NOT_FOUND_MESSAGE}
            </Text>
          )}

          {game && (
            <>
              <Heading size="6">{game.name}</Heading>

              {/* Keyed to the game: the page survives a :gameId change, so without it the form
                  keeps the previous game's colours and a save writes them onto this one. */}
              <GameBrandingForm key={game.id} game={game} />

              <div className="section-separator" />

              <PromotionalMessageList gameId={game.id} />
            </>
          )}
        </Flex>
      </Section>
    </Container>
  )
}

/**
 * The branding fields for one game. Split out so the form state starts from a game that has
 * already loaded, rather than being seeded after the fact.
 */
const GameBrandingForm = ({ game }: { game: EditableGame }) => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const queryClient = useQueryClient()
  const [form, setForm] = useState<GameBrandingFormData>(() => toBrandingFormData(game))
  const [errors, setErrors] = useState<BrandingErrors>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const brandingMutation = useMutation({
    mutationFn: (branding: GameBrandingUpdateRequest) =>
      GamesServiceAPI.updateGameBranding(game.id, branding),
    onSuccess: () => {
      setSaveError(null)
      setSaved(true)
      queryClient.invalidateQueries({ queryKey: editableGamesQueryKey(activeCustomerId) })
    },
    onError: async (error: unknown) => {
      setSaved(false)
      setSaveError(await readErrorMessage(error, SAVE_FAILED_MESSAGE))
    },
  })

  const validateColor = (field: ColorField) =>
    setErrors((current) => ({
      ...current,
      [field]: isValidHexColor(form[field]) ? undefined : HEX_COLOR_ERROR,
    }))

  const handleSaveAppearance = () => {
    const invalid = COLOR_FIELDS.filter((field) => !isValidHexColor(form[field]))
    setErrors(Object.fromEntries(invalid.map((field) => [field, HEX_COLOR_ERROR])))
    if (invalid.length > 0) {
      return
    }

    // Every field goes on every save; a field left blank is sent as null, which clears it.
    const payload: GameBrandingUpdateRequest = {
      logoUrl: blankToNull(form.logoUrl),
      specialBallColor: blankToNull(form.specialBallColor),
      brandColorPrimary: blankToNull(form.brandColorPrimary),
      fontColorPrimary: blankToNull(form.fontColorPrimary),
    }

    setSaved(false)
    brandingMutation.mutate(payload)
  }

  const handleChange = (updated: GameBrandingFormData) => {
    setForm(updated)
    setSaved(false)
  }

  return (
    <AppearanceSection
      gameName={game.name}
      form={form}
      errors={errors}
      isSaving={brandingMutation.isPending}
      saveError={saveError}
      isSaved={saved}
      onChange={handleChange}
      onBlurColor={validateColor}
      onSave={handleSaveAppearance}
    />
  )
}
